import React, { ChangeEvent } from 'react';
import {
  BoxActionContainer,
  ChipContainer,
  ChipText,
  ChipTitle,
  Container,
  ProductHeaderTitle,
  RowActionContainer,
  Text
} from './style';

import { Checkbox, Button, Title, Icon } from '@gerdau/hefesto/';
import { Tag } from '@gerdau/hefesto-mais-web/';
import {
  CartOptions,
  MeasureUnit,
  NewCartItem,
  UpdateItemProps
} from '../../../../../services/MVP/models';
import { formatCurrency } from '../../../../../utils/formatNumbers';
import { isDateExpired } from '../validate';
import moment from 'moment';
import {
  ChipInputs,
  DateInput,
  OptionsInput,
  QuantityInput
} from '../InputsOptions';
import { OptionsProps } from '.';
import {
  parseNumber,
  paymentAnticipated
} from '../../../../../services/MVP/utils';
import { normalizeQuantity } from '../../../../../utils/purchaseHelpers';

interface InformationProps {
  title: string;
  text: string;
  canShow: {
    time: number;
    personalize: boolean;
    minQty: boolean;
  };
}

const SkuAgreementPriceChip = ({
  sku,
  date,
  agreement
}: {
  sku: string;
  date?: string | Date;
  agreement?: boolean;
}) => {
  return (
    <ChipContainer>
      <ChipText>Cód. Gerdau: {sku}</ChipText>
      {!date || isDateExpired(date) || !agreement ? null : (
        <ChipTitle>Preço acordado</ChipTitle>
      )}
    </ChipContainer>
  );
};

const ItemPrice = ({
  price,
  onLoadNewPrice,
  renderLoadNewPriceButton
}: {
  price: string;
  renderLoadNewPriceButton: boolean;
  onLoadNewPrice: (props: OptionsProps) => void;
}) => {
  if (!price) {
    return null;
  }

  if (renderLoadNewPriceButton) {
    return (
      <Text>
        Preço do item:{' '}
        <Button
          variant="secondary"
          onClick={() => onLoadNewPrice({ updatePrice: true })}
          name="loadNewPriceButton"
        >
          Atualizar preços
        </Button>
      </Text>
    );
  }

  return (
    <Text>
      Preço do item: <Text bold>R$ {price}</Text>
    </Text>
  );
};

const Product = ({
  selected,
  product,
  dirtyProduct,
  handleUpdateItem,
  handleSelectProduct,
  handleDuplicateItem,
  handleDeleteItem,
  handleUpdateAllItems,
  isBlockedCustomer,
  loading,
  minDate,
  hasSomeProductsWithBenefit
}: {
  selected: boolean;
  product: NewCartItem;
  dirtyProduct?: NewCartItem;
  handleUpdateItem: (props: UpdateItemProps) => void;
  handleUpdateAllItems: (props: OptionsProps) => void;
  handleSelectProduct: (
    e: ChangeEvent<HTMLInputElement>,
    item_id: number
  ) => void;
  handleDuplicateItem: (id: number) => void;
  handleDeleteItem: (id: number) => void;
  isBlockedCustomer: boolean;
  loading: boolean;
  minDate: Date;
  hasSomeProductsWithBenefit: boolean;
}) => {
  if (Object.values(product.cartOptions).length === 0) {
    return null;
  }

  const [information, setInformation] = React.useState({
    title: '',
    text: '',
    canShow: {
      time: 0,
      personalize: false,
      minQty: false
    }
  });

  const changeInformation = (newInformation: InformationProps) => {
    setInformation((prev) => ({
      ...prev,
      ...newInformation
    }));
  };

  const destinationOptions = Object.values(product.cartOptions).map(
    (destinationOpt: CartOptions) => ({
      value: destinationOpt.destination.code,
      label: destinationOpt.destination.description
    })
  );

  const price = product.price;

  const destinationPrice = price.destination ?? destinationOptions[0].value;

  const destination =
    product.extension_attributes?.destination_gerdau ?? destinationPrice;

  const unity = product.extension_attributes.measure_unity_gerdau;

  const optionsByDestination = product.cartOptions[destination];

  if (!destination || !optionsByDestination || !unity) {
    return null;
  }

  const undOpt = optionsByDestination.measureUnits.map((opt) => ({
    id: opt.measure,
    label: opt.measure
  }));

  const currentMeasureUnit = optionsByDestination.measureUnits.find(
    (unit) => unit.measure === unity
  ) as MeasureUnit;

  const paymentOpt = optionsByDestination.paymentTerms
    .filter((opt) =>
      isBlockedCustomer ? paymentAnticipated.includes(opt.term) : opt
    )
    .map((opt) => ({
      value: opt.term,
      label: opt.description
    }));

  const optionsFiscalBenefits =
    optionsByDestination.fiscalBenefits.length > 0
      ? optionsByDestination.fiscalBenefits.filter(
          (tax) => tax.description.length !== 0
        )
      : [];

  const taxOpt =
    optionsFiscalBenefits.length === 0
      ? [{ value: 'Indisponível', label: 'Indisponível' }]
      : [
          { code: 'nenhum', description: 'Nenhum' },
          ...optionsFiscalBenefits
        ].map((benefits) => ({
          value: benefits.code,
          label: benefits.description
        }));

  const productDate = product.extension_attributes.expected_date_gerdau
    ? product.extension_attributes.expected_date_gerdau.replace('Z', '')
    : product.extension_attributes.expected_date_gerdau;

  const disabled = loading || !destination;
  const item_id = product.item_id;
  const errors = product.errors;

  const anyErrors = () => {
    if (Object.values(errors ?? {}).some((error) => error)) {
      return 'withSpace';
    }
    return 'withoutSpace';
  };
  const salesAgreementData = price.salesAgreementData;
  //Data da industria. Foi acordado usar apenas D+1 pois no caso de CG estava vindo a data atual
  // const minDate = optionsByDestination.minimumDate
  //   ? optionsByDestination.minimumDate.replace('Z', '')
  //   : optionsByDestination.minimumDate;

  const endDate = salesAgreementData.expirationDate
    ? salesAgreementData.expirationDate.replace('Z', '')
    : salesAgreementData.expirationDate;

  const image =
    product.media_gallery_entries && product.media_gallery_entries.length > 0
      ? `${process.env.REACT_APP_MEDIA_BASE_URL}/${product.media_gallery_entries[0]?.file}?width=56`
      : 'https://placehold.co/56x56/png';

  // const hasAgreement = product.extension_attributes?.has_agreed_price_gerdau;
  const salesAgreementExpirationDate = !moment(productDate).isAfter(endDate);

  const agreementData = {
    destination: salesAgreementData.destination,
    payment: salesAgreementData.paymentTerms,
    expirationDate: endDate
  };

  const hasAgreement = () => {
    const hasAnyAgreementKeyEmpty = Object.values(agreementData).some(
      (item) => !item
    );
    if (salesAgreementData && !hasAnyAgreementKeyEmpty) {
      return (
        salesAgreementData.destination === destination &&
        salesAgreementData.paymentTerms ===
          product.extension_attributes.payment_type_gerdau &&
        salesAgreementExpirationDate
      );
    }

    return false;
  };

  const priceWithoutMeasure =
    hasAgreement() && salesAgreementData.salesAgreement
      ? salesAgreementData.salesAgreement
      : Number(price.totalPrice ?? price.creditPrice) /
        normalizeQuantity(price.quantity);

  const convertPriceMeasure = optionsByDestination.measureUnits.find(
    (priceOpt) => priceOpt.measure === price.um
  );

  const priceKg =
    priceWithoutMeasure /
    parseNumber(convertPriceMeasure?.convMeasureKg ?? '1');

  const measureUnitminQty = Number(currentMeasureUnit.minimumQuantity);
  const minimunOrderQty =
    measureUnitminQty !== 1 && product.qty === 1
      ? Math.ceil(measureUnitminQty / Number(currentMeasureUnit.multiple)) *
        Number(currentMeasureUnit.multiple)
      : Number(currentMeasureUnit.multiple) * product.qty;

  const multipleWithKG =
    parseNumber(currentMeasureUnit.convMeasureKg) * minimunOrderQty;

  const itemPrice = priceKg * multipleWithKG;

  const dirtyExtensionAttributes = dirtyProduct?.extension_attributes;
  const showLoadNewPriceButton =
    (dirtyExtensionAttributes?.expected_date_gerdau &&
      dirtyExtensionAttributes?.expected_date_gerdau !== '' &&
      moment(productDate).format('YYYY-MM-DD') !==
        moment(
          dirtyExtensionAttributes?.expected_date_gerdau.replace('Z', '')
        ).format('YYYY-MM-DD')) ||
    destination !== dirtyExtensionAttributes?.destination_gerdau ||
    product.extension_attributes.payment_type_gerdau !==
      dirtyExtensionAttributes.payment_type_gerdau ||
    product.extension_attributes.tax_benefit_gerdau !==
      dirtyExtensionAttributes.tax_benefit_gerdau ||
    (salesAgreementData && !!endDate && moment(productDate).isAfter(endDate));

  return (
    <Container spacing={anyErrors()} bgWhite key={item_id}>
      <RowActionContainer withoutPadding>
        <BoxActionContainer productHeader>
          <Checkbox
            name="produtos"
            checked={selected}
            onChange={(e) =>
              product.item_id && handleSelectProduct(e, product.item_id)
            }
            disabled={disabled}
          />
          <img width="56" height="56" src={image} alt={product.name} />
          <ProductHeaderTitle>
            <Tag variant={'neutral-inverse'}>
              {product.descricao_gpm?.toUpperCase()}
            </Tag>
            <Title level={4}>{product.name}</Title>
          </ProductHeaderTitle>
        </BoxActionContainer>
        <SkuAgreementPriceChip
          sku={product.sku}
          agreement={hasAgreement()}
          date={endDate}
        />
      </RowActionContainer>

      <RowActionContainer optionsGap>
        <BoxActionContainer chipStrategy>
          <ChipInputs
            label="Unidade de medida"
            disabled={disabled}
            options={undOpt}
            onChange={handleUpdateItem}
            value={[unity ?? '']}
            item_id={item_id ?? 0}
            measureUnit={currentMeasureUnit}
            allMeasureUnit={optionsByDestination.measureUnits}
            information={information}
            changeInformation={changeInformation}
          />
        </BoxActionContainer>

        <BoxActionContainer hiddenFiscalBenefit={!hasSomeProductsWithBenefit}>
          <QuantityInput
            key={product.extension_attributes.measure_unity_gerdau}
            label="Quantidade"
            value={product.qty}
            disabled={disabled}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            measureUnit={currentMeasureUnit}
            units={optionsByDestination.measureUnits}
            information={information}
            changeInformation={changeInformation}
          />
          <DateInput
            error={errors?.date}
            label="Data desejada"
            value={productDate}
            minDate={minDate}
            endDate={endDate ? moment(endDate).toDate() : undefined}
            disabled={disabled || selected}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            hasAgreement={hasAgreement()}
            disableWeekend
          />
          <OptionsInput
            key={product.extension_attributes.destination_gerdau}
            error={errors?.destination}
            type="destination"
            label="Destinação"
            value={destination}
            options={destinationOptions}
            disabled={loading || selected}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            hasAgreement={hasAgreement()}
          />
          <OptionsInput
            key={product.extension_attributes.payment_type_gerdau}
            error={errors?.payment}
            type="payment"
            label="Pagamento"
            value={product.extension_attributes.payment_type_gerdau}
            options={paymentOpt}
            disabled={disabled || selected}
            onChange={handleUpdateItem}
            item_id={item_id ?? 0}
            hasAgreement={hasAgreement()}
          />
          {hasSomeProductsWithBenefit && (
            <OptionsInput
              key={product.extension_attributes.tax_benefit_gerdau}
              error={errors?.taxBenefit}
              type="taxBenefit"
              label="Benefício fiscal"
              value={
                optionsFiscalBenefits.length === 0
                  ? 'Indisponível'
                  : product.extension_attributes.tax_benefit_gerdau ?? 'nenhum'
              }
              options={taxOpt}
              disabled={
                disabled || optionsFiscalBenefits.length === 0 || selected
              }
              onChange={handleUpdateItem}
              item_id={item_id ?? 0}
              hasAgreement={hasAgreement()}
            />
          )}
        </BoxActionContainer>
      </RowActionContainer>

      <RowActionContainer withoutPadding priceContainer>
        <ItemPrice
          price={formatCurrency(itemPrice.toFixed(2))}
          onLoadNewPrice={handleUpdateAllItems}
          renderLoadNewPriceButton={showLoadNewPriceButton}
        />
        <BoxActionContainer>
          <Button
            name="Aplicar"
            variant="secondary"
            onClick={() => item_id && handleDuplicateItem(item_id)}
            disabled={disabled}
          >
            Duplicar
          </Button>
          <Button
            name="Deletar"
            variant="danger"
            icon={<Icon option="Trash" />}
            onClick={() => item_id && handleDeleteItem(item_id)}
            disabled={disabled}
          />
        </BoxActionContainer>
      </RowActionContainer>
    </Container>
  );
};

export default Product;
